import { inboundSchema, nextRequestId, PROTOCOL_VERSION, type Evt, type Req } from '../envelope';
import { NO_CAPS, type Capabilities, type DeviceProfile, type HapticKind, type PlatformBridge } from '../PlatformBridge';
import { WebBridge } from './web';

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(msg: string): void };
    /**
     * 네이티브 → 웹 진입점. RN 쪽에서 injectJavaScript 로 이 함수를 호출한다.
     *
     * window.addEventListener('message') 를 쓰지 않는 이유:
     *  1) iOS/Android 에서 동작이 갈리고 (document vs window)
     *  2) 같은 채널을 서드파티 스크립트가 쓸 수 있어 오염 가능성이 있다.
     * 전용 전역 함수 한 개가 훨씬 안정적이고 검증하기 쉽다.
     */
    __cbmiBridge?: { receive(raw: unknown): void };
  }
}

const REQUEST_TIMEOUT_MS = 5_000;
const HANDSHAKE_TIMEOUT_MS = 1_500;

export class RNWebViewBridge implements PlatformBridge {
  readonly platform = 'rn-webview' as const;
  capabilities: Capabilities = { ...NO_CAPS };

  /** 네이티브가 어떤 기능도 못 주면 여기로 흘려보낸다. */
  #fallback = new WebBridge();
  #pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }>();
  #listeners = new Set<(e: Evt) => void>();

  async init(): Promise<void> {
    await this.#fallback.init();
    window.__cbmiBridge = { receive: (raw) => this.#receive(raw) };

    try {
      const caps = await this.#request<Partial<Capabilities>>('handshake', { protocol: PROTOCOL_VERSION }, HANDSHAKE_TIMEOUT_MS);
      this.capabilities = { ...NO_CAPS, ...caps };
    } catch {
      // 네이티브가 응답하지 않음 = 브리지 없는 일반 WebView. 전부 웹 폴백으로.
      this.capabilities = { ...this.#fallback.capabilities, reminders: false };
    }
  }

  #post(env: Req): void {
    window.ReactNativeWebView?.postMessage(JSON.stringify(env));
  }

  #request<T>(method: string, params?: unknown, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = nextRequestId();
      const timer = window.setTimeout(() => {
        this.#pending.delete(id);
        reject(new Error(`bridge timeout: ${method}`));
      }, timeoutMs);
      this.#pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      this.#post({ v: PROTOCOL_VERSION, kind: 'req', id, method, ...(params === undefined ? {} : { params }) });
    });
  }

  #receive(raw: unknown): void {
    // 네이티브가 문자열로 줄 수도, 객체로 줄 수도 있다. 둘 다 받아준다.
    let value: unknown = raw;
    if (typeof raw === 'string') {
      try { value = JSON.parse(raw); } catch { return; }
    }
    const parsed = inboundSchema.safeParse(value);
    if (!parsed.success) return; // 미지의 메시지는 조용히 버린다

    const msg = parsed.data;
    if (msg.kind === 'evt') {
      for (const l of this.#listeners) l(msg);
      return;
    }

    const entry = this.#pending.get(msg.id);
    if (!entry) return; // 이미 타임아웃된 응답
    this.#pending.delete(msg.id);
    clearTimeout(entry.timer);
    if (msg.ok) entry.resolve(msg.result);
    else entry.reject(new Error(`${msg.error.code}: ${msg.error.message}`));
  }

  async keepAwake(on: boolean): Promise<void> {
    if (!this.capabilities.keepAwake) return this.#fallback.keepAwake(on);
    await this.#request('keepAwake', { on }).catch(() => this.#fallback.keepAwake(on));
  }

  async setImmersive(on: boolean): Promise<void> {
    if (!this.capabilities.immersive) return this.#fallback.setImmersive(on);
    await this.#request('setImmersive', { on }).catch(() => this.#fallback.setImmersive(on));
  }

  async haptic(kind: HapticKind): Promise<void> {
    if (!this.capabilities.haptics) return this.#fallback.haptic(kind);
    await this.#request('haptic', { kind }).catch(() => undefined);
  }

  async exportFile(name: string, mime: string, data: Blob): Promise<void> {
    if (!this.capabilities.fileExport) return this.#fallback.exportFile(name, mime, data);
    // WebView 채널은 문자열만 통과한다. base64 로 실어 보낸다.
    const b64 = await blobToBase64(data);
    await this.#request('exportFile', { name, mime, base64: b64 }).catch(() =>
      this.#fallback.exportFile(name, mime, data),
    );
  }

  async getDeviceProfile(): Promise<DeviceProfile> {
    const web = await this.#fallback.getDeviceProfile();
    if (!this.capabilities.deviceProfile) return { ...web, platform: this.platform };
    try {
      const native = await this.#request<Partial<DeviceProfile>>('getDeviceProfile');
      return { ...web, ...native, platform: this.platform };
    } catch {
      return { ...web, platform: this.platform };
    }
  }

  onNativeEvent(cb: (e: Evt) => void): () => void {
    this.#listeners.add(cb);
    const offWeb = this.#fallback.onNativeEvent(cb);
    return () => { this.#listeners.delete(cb); offWeb(); };
  }

  dispose(): void {
    for (const { timer, reject } of this.#pending.values()) {
      clearTimeout(timer);
      reject(new Error('bridge disposed'));
    }
    this.#pending.clear();
    this.#listeners.clear();
    delete window.__cbmiBridge;
    this.#fallback.dispose();
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('blob read failed'));
    r.onload = () => {
      const s = String(r.result);
      resolve(s.slice(s.indexOf(',') + 1));
    };
    r.readAsDataURL(blob);
  });
}
